import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  cancelMyRosterChangeRequestApi,
  createMyRosterChangeRequestApi,
  getMyRosterApi,
  getMyRosterChangeRequestsApi,
  MyRoster,
  MyRosterChangeRequest,
} from "../api/meApi";
import EmployeeShell from "../components/EmployeeShell";
import { COLABORADOR_HOME } from "../routes";
import { useGoBack } from "../../../hooks/useGoBack";
import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";

import "./Colaborador.css";
import "../../dp/pages/Roster.css";

// Semana da empresa: segunda a domingo (decisão do usuário, 03/10/2026).
const WEEKDAYS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
const KIND_LABEL: Record<string, string> = {
  FOLGA: "Folga",
  FERIADO: "Feriado",
  FERIAS: "Férias",
  AFASTAMENTO: "Afastado",
  LOJA_FECHADA: "Fechada",
};
const SHIFT_LABEL: Record<string, string> = { MANHA: "Manhã", NOITE: "Noite" };
const STATUS: Record<MyRosterChangeRequest["status"], { label: string; tone: string }> = {
  PENDENTE: { label: "Aguardando o DP", tone: "colabBadge--orange" },
  APROVADO: { label: "Aprovado", tone: "colabBadge--green" },
  REJEITADO: { label: "Recusado", tone: "colabBadge--gray" },
  CANCELADO: { label: "Cancelado", tone: "colabBadge--gray" },
};

const hhmm = (m: number | null) => (m === null ? "" : `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`);
const formatDate = (value: string) => new Date(value).toLocaleDateString("pt-BR", { timeZone: "UTC", weekday: "short", day: "2-digit", month: "2-digit" });
const todayIso = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" });

/**
 * A escala do mês do próprio colaborador: em que dias trabalha (e em que turno) e
 * em que dias folga. Só aparece depois que o DP publica. Daqui ele pode pedir para
 * trocar uma folga por um dia de trabalho; o DP aprova ou recusa.
 */
export default function MyRosterPage() {
  const goBack = useGoBack(COLABORADOR_HOME());
  const now = new Date();
  const [ym, setYm] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });
  const [roster, setRoster] = useState<MyRoster | null>(null);
  const [requests, setRequests] = useState<MyRosterChangeRequest[]>([]);
  const [asking, setAsking] = useState(false);
  const [form, setForm] = useState({ fromDate: "", toDate: "", reason: "" });
  const [sending, setSending] = useState(false);
  const [cancelId, setCancelId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setRoster(null);
    try {
      const [r, q] = await Promise.all([getMyRosterApi(ym.year, ym.month), getMyRosterChangeRequestsApi()]);
      setRoster(r);
      setRequests(q);
    } catch (error: any) {
      toast.error(error?.message || "Não foi possível carregar a sua escala.");
      setRoster({ year: ym.year, month: ym.month, published: false, stores: [], days: [] });
    }
  }, [ym]);

  useEffect(() => {
    load();
  }, [load]);

  const shiftMonth = (delta: number) => {
    const d = new Date(ym.year, ym.month - 1 + delta, 1);
    setYm({ year: d.getFullYear(), month: d.getMonth() + 1 });
  };

  const byDate = useMemo(() => new Map((roster?.days ?? []).map((d) => [d.date.slice(0, 10), d])), [roster]);
  const lastDay = new Date(ym.year, ym.month, 0).getDate();
  const firstWeekday = (new Date(Date.UTC(ym.year, ym.month - 1, 1)).getUTCDay() + 6) % 7;
  const today = todayIso();
  const keyOf = (n: number) => `${ym.year}-${String(ym.month).padStart(2, "0")}-${String(n).padStart(2, "0")}`;

  const future = (roster?.days ?? []).filter((d) => d.date.slice(0, 10) > today);
  const offDays = future.filter((d) => d.kind === "FOLGA");
  const workDays = future.filter((d) => d.kind === "TRABALHO");
  const worked = (roster?.days ?? []).filter((d) => d.kind === "TRABALHO").length;
  const rests = (roster?.days ?? []).filter((d) => d.kind !== "TRABALHO").length;

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.fromDate || !form.toDate) return toast.error("Escolha a folga e o dia de trabalho.");
    setSending(true);
    try {
      await createMyRosterChangeRequestApi({ fromDate: form.fromDate, toDate: form.toDate, reason: form.reason.trim() || undefined });
      toast.success("Pedido enviado ao DP.");
      setAsking(false);
      setForm({ fromDate: "", toDate: "", reason: "" });
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Não foi possível enviar o pedido.");
    } finally {
      setSending(false);
    }
  };

  const cancel = async (id: string) => {
    setCancelId(null);
    try {
      await cancelMyRosterChangeRequestApi(id);
      toast.success("Pedido cancelado.");
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Não foi possível cancelar.");
    }
  };

  const monthName = new Date(ym.year, ym.month - 1, 1).toLocaleDateString("pt-BR", { month: "long", year: "numeric" });

  return (
    <EmployeeShell>
      <section className="colabPanel">
        <div className="colabPageTitle">
          <h2 className="colabPanel__title" style={{ margin: 0 }}>
            Minha escala
          </h2>
          <button type="button" className="colabBtn colabBtn--ghost colabBtn--compact" onClick={goBack}>
            Voltar
          </button>
        </div>

        <div className="colabMonthNav">
          <button type="button" className="colabBtn colabBtn--ghost colabBtn--icon" onClick={() => shiftMonth(-1)} aria-label="Mês anterior">
            ‹
          </button>
          <strong>{monthName}</strong>
          <button type="button" className="colabBtn colabBtn--ghost colabBtn--icon" onClick={() => shiftMonth(1)} aria-label="Próximo mês">
            ›
          </button>
        </div>

        {roster === null ? (
          <p className="colabLoading">Carregando...</p>
        ) : !roster.published ? (
          <p className="colabHint">A escala deste mês ainda não foi publicada pelo DP.</p>
        ) : (
          <>
            <p className="colabHint">
              {roster.stores.map((s) => s.name).join(" e ")} · {worked} dia(s) de trabalho e {rests} de descanso no mês.
            </p>
            <div className="rosterLegend">
              <span><i className="rosterSwatch rosterCell--MANHA" /> Manhã</span>
              <span><i className="rosterSwatch rosterCell--NOITE" /> Noite</span>
              <span><i className="rosterSwatch rosterCell--FOLGA" /> Folga</span>
              <span><i className="rosterSwatch rosterCell--FERIAS" /> Férias</span>
              <span><i className="rosterSwatch rosterCell--AFASTAMENTO" /> Afastamento</span>
            </div>
            <div className="rosterCalendar">
              {WEEKDAYS.map((w) => (
                <div key={w} className="rosterCalendar__weekday">
                  {w}
                </div>
              ))}
              {Array.from({ length: firstWeekday }, (_, i) => (
                <div key={`pad-${i}`} />
              ))}
              {Array.from({ length: lastDay }, (_, i) => {
                const key = keyOf(i + 1);
                const d = byDate.get(key);
                const variant = !d ? "" : d.kind === "TRABALHO" ? `rosterCell--${d.shift ?? "MANHA"}` : `rosterCell--${d.kind}`;
                return (
                  <div key={key} className={`rosterCalendar__day ${variant} ${key === today ? "is-today" : ""}`}>
                    <strong>{i + 1}</strong>
                    {d ? (
                      d.kind === "TRABALHO" ? (
                        <>
                          <span>{SHIFT_LABEL[d.shift ?? ""] ?? "Trabalho"}</span>
                          <span>
                            {hhmm(d.startMinute)}–{hhmm(d.endMinute)}
                          </span>
                        </>
                      ) : (
                        <span>{KIND_LABEL[d.kind]}</span>
                      )
                    ) : null}
                  </div>
                );
              })}
            </div>

            <div style={{ marginTop: 16 }}>
              {!asking ? (
                <button
                  type="button"
                  className="colabBtn colabBtn--primary colabBtn--compact"
                  onClick={() => setAsking(true)}
                  disabled={!offDays.length || !workDays.length}
                  title={!offDays.length ? "Sem folgas futuras neste mês" : undefined}
                >
                  Pedir troca de folga
                </button>
              ) : (
                <form onSubmit={send} className="colabListItem">
                  <div className="colabField" style={{ marginBottom: 12 }}>
                    <label htmlFor="swapFrom">Quero trabalhar na folga de</label>
                    <select id="swapFrom" className="colabInput" value={form.fromDate} onChange={(e) => setForm({ ...form, fromDate: e.target.value })}>
                      <option value="">Escolha</option>
                      {offDays.map((d) => (
                        <option key={d.date} value={d.date.slice(0, 10)}>
                          {formatDate(d.date)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="colabField" style={{ marginBottom: 12 }}>
                    <label htmlFor="swapTo">E folgar em</label>
                    <select id="swapTo" className="colabInput" value={form.toDate} onChange={(e) => setForm({ ...form, toDate: e.target.value })}>
                      <option value="">Escolha</option>
                      {workDays.map((d) => (
                        <option key={d.date} value={d.date.slice(0, 10)}>
                          {formatDate(d.date)} · {SHIFT_LABEL[d.shift ?? ""] ?? ""}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="colabField" style={{ marginBottom: 12 }}>
                    <label htmlFor="swapReason">Motivo (opcional)</label>
                    <input id="swapReason" className="colabInput" maxLength={300} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button type="submit" className="colabBtn colabBtn--primary colabBtn--compact" disabled={sending}>
                      Enviar ao DP
                    </button>
                    <button type="button" className="colabBtn colabBtn--ghost colabBtn--compact" onClick={() => setAsking(false)} disabled={sending}>
                      Cancelar
                    </button>
                  </div>
                </form>
              )}
            </div>
          </>
        )}

        {requests.length ? (
          <>
            <h3 className="colabPanel__title" style={{ marginTop: 20 }}>
              Meus pedidos de troca
            </h3>
            <div className="colabList">
              {requests.map((r) => (
                <article key={r.id} className="colabListItem">
                  <div className="colabListItem__head">
                    <span>
                      Trabalhar {formatDate(r.fromDate)} · folgar {formatDate(r.toDate)}
                    </span>
                    <span className={`colabBadge ${STATUS[r.status].tone}`}>{STATUS[r.status].label}</span>
                  </div>
                  {r.decisionNote ? <p className="colabListItem__meta">DP: {r.decisionNote}</p> : null}
                  {r.status === "PENDENTE" ? (
                    <button type="button" className="colabBtn colabBtn--ghost colabBtn--compact" style={{ marginTop: 8 }} onClick={() => setCancelId(r.id)}>
                      Cancelar pedido
                    </button>
                  ) : null}
                </article>
              ))}
            </div>
          </>
        ) : null}
      </section>
      <EcoConfirmDialog
        open={Boolean(cancelId)}
        title="Cancelar o pedido?"
        message="O DP deixa de ver este pedido de troca. Você pode fazer outro depois."
        confirmLabel="Cancelar pedido"
        tone="danger"
        onConfirm={() => cancelId && cancel(cancelId)}
        onCancel={() => setCancelId(null)}
      />
    </EmployeeShell>
  );
}
